import { EllipsisOutlined, UploadOutlined } from '@ant-design/icons';
import { Plus } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import ScrollView from '@/components/Common/ScrollView';
import DataTable from '@/components/DataTable';
import ColumnCustomizer from '@/components/DataTable/ColumnCustomizer';
import Sorter from '@/components/DataTable/Sorter';
import SearchInput from '@/components/Inputs/SearchInput';
import StyledPagination from '@/components/Pagination/Pagination';
import type { TriggerPos } from '@/components/Select/types';
import { PageHeaderTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import TABLE_KEY from '@/configs/table-key';
import useFeedbackListTableHeader, {
  useFeedbackTableData,
} from '@/data-table-configs/feedback';
import DatePresetFilter, {
  type DateFilterState,
} from '@/features/Commons/DatePresetFilter';
import ActionModalGuide from '@/features/Commons/Guide/ActionModalGuide';
import PageHeaderStyle from '@/features/Commons/PageLayout/PageHeader';
import FeedbackActionModal from '@/features/Feedback/components/FeedbackActionModal/FeedbackActionModal';
import { useStructuredStorageFilterQuery } from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useColor from '@/hooks/domain/useColor';
import { useCommonDateState } from '@/hooks/domain/useCommonDateState';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useFetch from '@/hooks/pure/useFetch';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import useFeedbackListStore, {
  FEEDBACK_LIST_PAGE_SIZE,
} from '@/stores/Feedback/FeedbackListStore';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { shadow } from '@/styles/style';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { track } from '@/utils/track/track';
import { addQuery } from '@/utils/util';

import { FEEDBACK_SORT_OPTIONS } from '../configs/feedback-sort-options';
import { useFeedbackSortState } from '../hooks/useFeedbackSortState';
import FeedbackEllipsisModal from './FeedbackEllipsisModal';
import FeedbackTrendChartArea from './FeedbackTrendChartArea';
import NewFeedbackNotificationModal from './NewFeedbackNotificationModal';
import useFeedbackListShowOptions from './useFeedbackListShowOptions';
import useFeedbackTrendChart from './useFeedbackTrendChartData';
import useFetchFeedbackList from './useFetchFeedbackList';

const initialActionModalProps = {
  status: false,
  triggerPos: {},
};

const FeedbackListView = () => {
  const { isInnerMember } = useUserRole();
  const {
    feedbackAliasesInPage: feedbacks,
    paginationInfo,
    listLoading,
    searchValue,
    setState: setFeedbackListStoreState,
    setPageAliases,
    pageAliases,
    setPagenationInfo,
    updateFetchParams,
  } = useFeedbackListStore(
    useShallow((state) => ({
      feedbackAliasesInPage: state.feedbackAliasesInPage,
      paginationInfo: state.paginationInfo,
      listLoading: state.listLoading,
      searchValue: state.searchValue,
      setState: state.setState,
      setPageAliases: state.setPageAliases,
      pageAliases: state.pageAliases,
      setPagenationInfo: state.setPaginationInfo,
      updateFetchParams: state.updateFetchParams,
    })),
  );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const color = useColor();
  const { navigate, push } = useWorkspaceRouter();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isEditor } = useUserRole();

  const { showOptions, setOption } = useFeedbackListShowOptions();
  const [actionMenuState, setActionMenuState] = useState<{
    triggerPos: TriggerPos;
    status: boolean;
  }>(initialActionModalProps);

  const [sortState, setSortState] = useFeedbackSortState();

  const onSortChange = (sort: NotNullableSortStatusT) => {
    setSortState(sort);
    updateFetchParams({
      sortStatus: sort,
    });
  };

  const [checkedList, setCheckedList] = useState<string[]>([]);

  const { filterQueryFormats, clearFilter, onFilterSetChange } =
    useStructuredStorageFilterQuery(STORAGE_KEYS.FEEDBACK_FILTER_STATUS_KEY);

  const { dateState, onDateFilterChange, dateRangeString } =
    useCommonDateState();

  const { toggleColumnVisible, changeColumnOrder, changeColumnWidth } =
    useColumnConfigStore(
      ({ toggleColumnVisible, changeColumnOrder, changeColumnWidth }) => ({
        toggleColumnVisible,
        changeColumnOrder,
        changeColumnWidth,
      }),
    );

  type State = {
    trendData: FeedbackListTrendDataT;
    isOpenEllipsisModal: boolean;
    selectedChartBar: SelectedChartBar;
  };

  const initialState: State = {
    trendData: {
      count: 0,
      feedback_trend: [],
    },
    isOpenEllipsisModal: false,
    selectedChartBar: null,
  };

  const { state, setValue } = useStates<State>(initialState);
  const { setNewModal } = useDetailModalStore();

  const columns = useFeedbackListTableHeader();

  const onRowClick = (feedbackAlias: string | null) => {
    if (feedbackAlias) {
      setNewModal({
        alias: feedbackAlias,
        type: DETAIL_MODAL_TYPE.FEEDBACK,
        idx: 0,
      });
      setPageAliases(feedbackAlias);
    }
  };

  const datas = useFeedbackTableData(feedbacks, {
    onRowClick: onRowClick,
    onRowRightClick(alias, e) {
      e.preventDefault();
      setActionMenuState({
        triggerPos: {
          left: e.clientX,
          top: e.clientY,
        },
        status: true,
      });
    },
  });

  const ellipsisButtonRef = useRef<HTMLButtonElement>(null);

  const {
    trendChartData,
    trendChartOptions,
    clearChartSelect,
    chartRef,
    backgroundChartArea,
  } = useFeedbackTrendChart({
    name: 'feedbackListView',
    rangeUnit: dateState.rangeUnit,
    trendData: state.trendData,
    dateRange: dateState.dateRange,
    selectedChartBar: state.selectedChartBar,
    setValue,
    ...showOptions,
  });

  const fetchTrend = useCallback(async () => {
    let newFilter: StructuredFilter | undefined =
      structuredFilterUtils.buildStructuredFilter(filterQueryFormats) || {};

    newFilter[FEEDBACK_COLUMNS.FEEDBACK_TEXT] =
      structuredFilterUtils.buildSingleValue(searchValue);

    const trend = await service.feedback.fetchFeedbackTrend({
      query: {
        view_type: dateState.rangeUnit.toString(),
      },
      payload: {
        ...newFilter,
        ...structuredFilterUtils.buildDateFilter(dateRangeString),
      },
    });
    setValue('trendData', trend);
  }, [
    JSON.stringify(filterQueryFormats),
    JSON.stringify(dateRangeString),
    dateState.rangeUnit,
    searchValue,
  ]);

  const openCreateFeedbackModal = () => {
    navigate(
      `${location.pathname}${addQuery(Object.fromEntries([...searchParams]), {
        key: 'feedback-create',
        value: 'true',
      })}`,
    );
  };

  const clearSearch = useCallback(
    (target: HTMLInputElement) => {
      target.value = '';
      setFeedbackListStoreState({ searchValue: '' });
    },
    [setFeedbackListStoreState],
  );

  const dateFilterChange = useCallback(
    (state: DateFilterState) => {
      onDateFilterChange(state);
      clearChartSelect();
    },
    [onDateFilterChange, clearChartSelect],
  );

  const onExportFeedbackListClick = async (
    reason?: string,
    password?: string,
  ) => {
    const structuredFilter =
      structuredFilterUtils.buildStructuredFilter(filterQueryFormats);
    await service.feedback.exportFeedbackList({
      reason,
      password,
      payload: {
        ...structuredFilter,
        [FEEDBACK_COLUMNS.FEEDBACK_TEXT]:
          structuredFilterUtils.buildSingleValue(searchValue),
        [FEEDBACK_COLUMNS.DATE_START]:
          dateState.dateRange?.[0].toJSON() || undefined,
        [FEEDBACK_COLUMNS.DATE_END]:
          dateState.dateRange?.[1].toJSON() || undefined,
      },
    });
  };

  useEffect(() => {
    return () => {
      if (searchInputRef.current) {
        clearSearch(searchInputRef.current);
      }
      setFeedbackListStoreState((prev) => {
        return {
          paginationInfo: {
            ...prev.paginationInfo,
            page: 0,
          },
        };
      });
    };
  }, [clearSearch, setFeedbackListStoreState]);

  const fetchList = useFetchFeedbackList({
    selectedChartBar: state.selectedChartBar,
    dateState,
    trendChartData,
    searchValue,
    sortStatus: sortState,
    filterQueryFormats,
    clearCheckList: () => setCheckedList([]),
  });

  useFetch(fetchTrend);

  return (
    <React.Fragment>
      <Style>
        <PageHeaderStyle>
          <Flex g={12} a="center" height="100%">
            <PageHeaderTitle>Feedback</PageHeaderTitle>
            <Typo color={color.gray600} type="label1">
              {paginationInfo.count}
            </Typo>
            <SearchInput
              ref={searchInputRef}
              placeholder="Search"
              hasValue={!!searchValue}
              onBlur={(e) => {
                const value = e.target.value;
                track(AMP_KEYS.FEEDBACK_LIST_VIEW_SEARCH, {
                  search: value,
                });
                setFeedbackListStoreState({ searchValue: value });
              }}
              onClickClear={() => {
                if (searchInputRef.current) {
                  clearSearch(searchInputRef.current);
                }
              }}
            />
          </Flex>
          {isEditor && (
            <Flex a="center" g="6px">
              {isInnerMember && (
                <Button
                  variant="secondary"
                  sizeType="m"
                  leftIcon={<UploadOutlined />}
                  onClick={async () => {
                    push('/feedbacks/upload-json');
                  }}
                >
                  Upload JSON
                </Button>
              )}
              <Button
                variant="secondary"
                sizeType="m"
                leftIcon={<UploadOutlined />}
                onClick={async () => {
                  track(AMP_KEYS.FEEDBACK_CSV_UPLOAD_CLICK);
                  push('/feedbacks/upload');
                }}
              >
                Upload
              </Button>
              <Button
                sizeType="m"
                leftIcon={<Plus />}
                onClick={() => {
                  track(AMP_KEYS.FEEDBACK_MANUAL_ADD_CLICK);
                  openCreateFeedbackModal();
                }}
              >
                Feedback
              </Button>
            </Flex>
          )}
        </PageHeaderStyle>
        <ScrollView style={{ height: 'calc(100% - 56px)' }}>
          <ChartControllerWrapper>
            <Flex j="space-between">
              <StructuredFilter
                values={filterQueryFormats}
                onChange={(values) => {
                  onFilterSetChange(values);
                }}
                clearFilter={() => {
                  clearFilter();
                  fetchTrend();
                }}
              />
              <Flex a="center" g="8px">
                <DatePresetFilter
                  dateState={dateState}
                  setDateState={dateFilterChange}
                  size="middle"
                />
                <IconButton
                  icon={<EllipsisOutlined />}
                  sizeType="m"
                  outlined={false}
                  onClick={() => setValue('isOpenEllipsisModal', true)}
                  ref={ellipsisButtonRef}
                />
              </Flex>
            </Flex>
          </ChartControllerWrapper>
          {showOptions.showChart && (
            <FeedbackTrendChartArea
              backgroundChartArea={backgroundChartArea}
              trendChartData={trendChartData}
              trendChartOptions={trendChartOptions}
              chartRef={chartRef}
              searchValue={searchValue}
              selectedChartBar={state.selectedChartBar}
              trendData={state.trendData}
              filteredCount={paginationInfo.count}
              {...showOptions}
            />
          )}
          <Flex j="flex-end" padding="0px 24px">
            <Sorter
              options={FEEDBACK_SORT_OPTIONS}
              currSortStatus={sortState}
              onSortChange={onSortChange}
            />
            <ColumnCustomizer
              columns={columns}
              onCustomizeColumnSelect={(key: string) => {
                toggleColumnVisible('feedback_view', key);
              }}
            />
          </Flex>
          <VFlex style={{ position: 'relative' }}>
            <ActionModalGuide
              status={checkedList.length > 0}
              length={checkedList.length}
              style={{ transform: 'translate(18px, 6px)' }}
              onActionClick={(e) => {
                setActionMenuState({
                  triggerPos: {
                    left: e.clientX,
                    top: e.clientY,
                  },
                  status: true,
                });
              }}
            />

            <DataTable
              name={TABLE_KEY.FEEDBACK_TABLE_KEY}
              tableHeight={
                showOptions.showChart
                  ? `calc(100vh - 450px)`
                  : `calc(100vh - 160px)`
              }
              id={TABLE_KEY.FEEDBACK_TABLE_KEY}
              datas={datas}
              columns={columns}
              selectedRowKey={pageAliases.currentAlias}
              sortStatus={sortState}
              onSortChange={onSortChange}
              loading={listLoading}
              asyncData={true}
              checkedList={checkedList}
              checkable={isEditor}
              customizable={true}
              onCheckListChange={setCheckedList}
              columnChangeOrderHandler={(key: string, order: number) =>
                changeColumnOrder('feedback_view', key, order)
              }
              columnResizeHandler={(key: string, width: number) =>
                changeColumnWidth('feedback_view', key, width)
              }
              onCustomizeColumnSelect={(key: string) =>
                toggleColumnVisible('feedback_view', key)
              }
              openButtonOffsetX={20}
            />
          </VFlex>
          <PagenationWrapper>
            <StyledPagination
              loading={listLoading}
              pageSize={FEEDBACK_LIST_PAGE_SIZE}
              currentLength={datas.length}
              total={paginationInfo.count}
              current={paginationInfo.page}
              onChange={fetchList}
            />
          </PagenationWrapper>
        </ScrollView>
      </Style>
      <NewFeedbackNotificationModal
        refetch={() => {
          fetchList();
          fetchTrend();
        }}
      />
      <FeedbackActionModal
        tableKey={TABLE_KEY.FEEDBACK_TABLE_KEY}
        feedbackAliases={checkedList}
        dropdownProps={{
          close: () =>
            setActionMenuState((prev) => ({
              ...prev,
              status: false,
            })),
          ...actionMenuState,
        }}
        clearCheckList={() => setCheckedList([])}
        onDeleteSuccess={(count: number) => {
          setPagenationInfo({
            ...paginationInfo,
            count: paginationInfo.count - count,
          });
          setValue('trendData', {
            ...state.trendData,
            count: state.trendData.count - count,
          });
        }}
      />
      <FeedbackEllipsisModal
        {...showOptions}
        setOption={setOption}
        onExportFeedbackListClick={onExportFeedbackListClick}
        dropdownProps={{
          triggerEl: ellipsisButtonRef.current,
          status: state.isOpenEllipsisModal,
          modalWidth: '164px',
          close: () => setValue('isOpenEllipsisModal', false),
        }}
      />
    </React.Fragment>
  );
};

const Style = styled(VFlex)`
  width: 100%;
  height: 100vh;
  position: relative;
  .feedback-detail-area {
    ${shadow.rightLine};
  }
`;

const ChartControllerWrapper = styled.div`
  padding: 0px 32px 16px 32px;
`;

const PagenationWrapper = styled(Flex).attrs({
  a: 'center',
  j: 'flex-start',
})`
  border-top: 1px solid ${(props) => props.theme.colors.ND04};
  width: 100%;
  height: 44x;
  padding: 0 32px;
  bottom: 0;
`;

export default FeedbackListView;
